import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';

import { routes } from './app.routes';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import {
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { environment } from '../environments/environment';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HomeService } from '../core/home/home.service';
import { firstValueFrom } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { Router } from '@angular/router';
import { SplashScreenService } from '../core/splash/splash-screen.service';
import { authInterceptor } from '../core/auth/auth.interceptor';
import { platformInterceptor } from '../core/platform/platform.interceptor';
import { AnalyticsService } from '../core/analytics/analytics.service';
// import { provideAnimations } from '@angular/platform-browser/animations';
registerLocaleData(localeEs, 'es');
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([authInterceptor, platformInterceptor])),
    // provideAnimations(),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'disabled' })
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideAnalytics(() => {
      const app = initializeApp(environment.firebase);
      return getAnalytics(app);
    }),
    ScreenTrackingService,
    UserTrackingService,
    provideAppInitializer(() => {
      // Inicializar Analytics Service para empezar a trackear rutas
      inject(AnalyticsService);

      // Esta función retorna un Promise que Angular ejecuta antes de arrancar la app
      const homeService = inject(HomeService);
      const router = inject(Router);
      const splash = inject(SplashScreenService);
      // Esperamos a que termine la llamada inicial:
      return firstValueFrom(homeService.get())
        .then((data: any) => {
          const status = data?.payload.configs?.status;
          const configs = data?.payload.configs || {};
          const currentPath = window.location.pathname;

          // Si estamos en la página de soporte, no redirigimos
          if (currentPath.startsWith('/support')) {
            // Permitir acceso a soporte independientemente del estado del backend
            return;
          }

          // Supongamos que el JSON trae un campo maintenance: boolean
          // Si back dice "mantenimiento":
          if (status === 'maintenance') {
            // y no estamos ya en /maintenance, vamos ahí
            if (!router.url.startsWith('/maintenance')) {
              router.navigate(['/maintenance']);
            }

            // Si back dice "postulación":
          } else if (status === 'postulation') {
            if (!router.url.startsWith('/postulation')) {
              router.navigate(['/postulation']);
            }

            // Si back dice "timer":
          } else if (status === 'timer' && configs.timer?.date) {
            // Verificar que la fecha del timer sea superior a hoy
            const timerDate = new Date(configs.timer.date);
            const today = new Date();

            if (timerDate > today) {
              if (!router.url.startsWith('/timer')) {
                router.navigate(['/timer']);
              }
            } else {
              // Si la fecha ya pasó, redirigir a home
              if (window.location.pathname.startsWith('/timer')) {
                router.navigate(['/home']);
              }
            }

            // En cualquier otro estado (sitio "bueno")
          } else {
            // Si estamos en /maintenance, /postulation o /timer, redirigimos a home
            if (
              window.location.pathname.startsWith('/maintenance') ||
              window.location.pathname.startsWith('/postulation') ||
              window.location.pathname.startsWith('/timer')
            ) {
              router.navigate(['/home']);
            }
            // (sino, dejamos la ruta que pidió el usuario)
          }
          setTimeout(() => {
            splash.hide();
          }, 1000);
          // si no está en mantenimiento, seguimos con normalidad
        })
        .catch((error) => {
          console.error(
            '%csrc/app/app.config.ts:66 error',
            'color: #007acc;',
            error
          );

          // Si estamos en la página de soporte, no redirigimos al error
          if (window.location.pathname.startsWith('/support')) {
            splash.hide();
            return;
          }

          router.navigate(['/maintenance']);
          // y ocultar el splash
          splash.hide();
        });
    }),
    { provide: LOCALE_ID, useValue: 'es' }, // para que date pipe use español
  ],
};
